import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, inject, input, signal, untracked } from '@angular/core';
import { Subscription } from 'rxjs';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';
import { BackendService } from '../../../services/backend';
import { ScanTag } from '../../../model';
import { MarkdownViewComponent } from '../markdown-view/markdown-view.component';
import { ScanTagsEditorWindowComponent } from '../scan-tags-editor-window/scan-tags-editor-window.component';
import { problemDetail } from '../tags/tag-rules';
import { TagsCacheService } from '../tags/tags-cache.service';

/**
 * The tags of one scan. Admin-only: this component does no role check itself - the place that puts it into a
 * page other roles can see (scan dialog Details block, gallery card) must gate it.
 *
 * Nothing is loaded until the user clicks "Show tags" (or `autoLoad` is set): then GET /scans/{scanId}/tags is
 * called and the button is replaced by the tag list + an "Edit" button that opens the editor window.
 * Descriptions in the tooltips come from the tag cache when it happens to be loaded already; this component
 * never triggers loading the cache itself (the editor window does).
 */
@Component({
  selector: 'app-scan-tags-list',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NzButtonModule, NzTagModule, NzTooltipModule, MarkdownViewComponent, ScanTagsEditorWindowComponent],
  template: `
    @if (scanTags() === null) {
      <button nz-button nzSize="small" type="button" class="show-btn" [nzLoading]="loading()" (click)="load()" i18n>Show tags</button>
      @if (error()) {
        <span class="error" role="alert">{{ error() }}</span>
      }
    } @else {
      <div class="list">
        @for (item of items(); track item.tagId) {
          <nz-tag
            class="scan-tag"
            nz-tooltip
            [nzTooltipTitle]="item.description?.trim() ? descriptionTpl : null"
            [nzTooltipTitleContext]="{ $implicit: item.description }"
            >{{ item.tagName }}</nz-tag
          >
        } @empty {
          <span class="muted" i18n>No tags</span>
        }
        <button nz-button nzSize="small" type="button" class="edit-btn" (click)="editorOpen.set(true)" i18n>Edit</button>
      </div>
      <ng-template #descriptionTpl let-description>
        <app-markdown-view [text]="description"></app-markdown-view>
      </ng-template>
    }
    @if (editorOpen()) {
      <app-scan-tags-editor-window
        [scanId]="scanId()"
        [currentTags]="scanTags() ?? []"
        (saved)="scanTags.set($event)"
        (closed)="editorOpen.set(false)"
      ></app-scan-tags-editor-window>
    }
  `,
  styles: [
    `
      .list {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 4px;
      }
      .scan-tag {
        margin: 0;
      }
      .muted {
        color: rgba(0, 0, 0, 0.45);
      }
      .error {
        margin-inline-start: 8px;
        color: #cf1322;
      }
    `,
  ],
})
export class ScanTagsListComponent {
  private readonly backend = inject(BackendService);
  private readonly cache = inject(TagsCacheService);
  private readonly destroyRef = inject(DestroyRef);

  readonly scanId = input.required<string>();
  /** Load immediately instead of waiting for the "Show tags" click (used by the popup window). */
  readonly autoLoad = input<boolean>(false);

  /** null = not loaded yet (the button is shown). */
  protected readonly scanTags = signal<ScanTag[] | null>(null);
  protected readonly loading = signal(false);
  protected readonly error = signal<string | null>(null);
  protected readonly editorOpen = signal(false);

  protected readonly items = computed(() => {
    const byId = this.cache.byId();
    return (this.scanTags() ?? []).map((scanTag) => ({ ...scanTag, description: byId.get(scanTag.tagId)?.tagDescription ?? null }));
  });

  private request: Subscription | null = null;

  constructor() {
    this.destroyRef.onDestroy(() => this.request?.unsubscribe());
    // The scan dialog re-uses this component while paging through scans: a new scan starts from scratch.
    effect(() => {
      this.scanId();
      const auto = this.autoLoad();
      untracked(() => {
        this.request?.unsubscribe();
        this.scanTags.set(null);
        this.error.set(null);
        this.loading.set(false);
        this.editorOpen.set(false);
        if (auto) this.load();
      });
    });
  }

  protected load(): void {
    if (this.loading()) return;
    this.loading.set(true);
    this.error.set(null);
    this.request?.unsubscribe();
    this.request = this.backend.getScanTags(this.scanId()).subscribe({
      next: (tags) => {
        this.scanTags.set(tags);
        this.loading.set(false);
      },
      error: (err) => {
        this.error.set(problemDetail(err, $localize`Loading the tags failed`));
        this.loading.set(false);
      },
    });
  }
}
